/**
 * Shared shell for the five Phase 3b auth screens — login, signup,
 * request-password-reset, confirm-password-reset, verify-email.
 *
 * Matches the Option A "centered card" mockup at
 * mockups/auth-login-option-a-centered.html.
 *
 * Semantic hierarchy: the SCREEN's title is the `<h1>` (the page's
 * primary heading); the brand block above is decorative — a styled
 * `<div>` rather than another heading. Screen readers announce the
 * action ("Sign in") as the document's H1, which matches what the
 * user is actually doing here.
 *
 * The layout is auth-state-unaware; each screen owns its form, error
 * surface, and any pre-form alerts (e.g., the post-reset banner).
 */
import { type ReactNode } from 'react'
import { Card, CardContent, CardHeader } from '@/components/ui'

export interface AuthLayoutProps {
  /** H1 inside the card — the action this screen represents. */
  title: string
  /** Sub-heading paragraph under the title (optional). */
  subtitle?: string
  /** Optional alert ribbon rendered above the title (e.g., "Password
   * updated. Sign in with the new one."). Pre-form, post-success
   * banners belong here. */
  banner?: ReactNode
  /** The form / inputs / buttons specific to this screen. */
  children: ReactNode
  /** Optional footer line below the card (e.g., signup CTA on the
   * login page). Plain ReactNode so callers can mix text and links. */
  footer?: ReactNode
}

export function AuthLayout({ title, subtitle, banner, children, footer }: AuthLayoutProps) {
  return (
    <div className="min-h-dvh flex items-center justify-center bg-muted/40 p-4">
      <div className="w-full max-w-sm space-y-6">
        {/* Decorative brand — not a heading. The action title below is the
            screen's actual H1. Same theme-aware wordmark swap as the
            top-left logo in the main app shell (see Header.tsx). */}
        <div className="text-center space-y-1" aria-hidden="true">
          <div className="flex justify-center">
            <img
              src="/brand/wordmark-light.png"
              alt=""
              className="h-9 w-auto block dark:hidden"
            />
            <img
              src="/brand/wordmark-dark.png"
              alt=""
              className="h-9 w-auto hidden dark:block"
            />
          </div>
          <p className="text-sm text-muted-foreground">Autonomous Quality Engineering</p>
        </div>

        {banner}

        <Card>
          <CardHeader className="space-y-1.5 pb-4">
            <h1 className="text-xl font-semibold">{title}</h1>
            {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
          </CardHeader>

          <CardContent className="space-y-4">
            {children}
          </CardContent>
        </Card>

        {footer && (
          <p className="text-center text-sm text-muted-foreground">
            {footer}
          </p>
        )}
      </div>
    </div>
  )
}
